import { useState, useRef, useMemo } from 'react';
import { useAdmin } from './AdminContext';
import { invokeFunction, supabase } from '../../supabase';
import { REALMS } from '../../../../shared/src/realms';
import {
  COVERAGE_TARGETS, SURFACE_RULE, TIER_MIX, TOPIC_CORE,
  realmTarget, poolTarget, tierOfTopic, type CoverageTarget,
} from '../../../../shared/src/taxonomyV2';
import { poolGaps, realmGap, type TopicGap } from './CoverageTargets';
import { CoverageGrid } from './CoverageGrid';

/**
 * The factory screen answers one question: what should I generate next.
 *
 * It used to be a box of controls — pick a realm, pick a branch, pick a
 * difficulty, pick a count — which meant the operator had to already know the
 * answer before the screen was any use. Now the gaps are computed against a
 * coverage target and the screen leads with them; the manual controls are still
 * there, underneath, for when you want something specific.
 */

const MAX_PER_CALL = 25;

interface GenJob { realm: string; topic: string; difficulty: string; count: number; label: string }

/** Split a topic's shortfall into per-difficulty jobs, capped per call. */
function jobsForGap(realm: string, t: TopicGap): GenJob[] {
  const out: GenJob[] = [];
  (['easy', 'medium', 'hard'] as const).forEach((d, i) => {
    let n = t.byDiff[i];
    while (n > 0) {
      const c = Math.min(MAX_PER_CALL, n);
      out.push({ realm, topic: t.topic, difficulty: d, count: c, label: `${realm} · ${t.topic} · ${d} ×${c}` });
      n -= c;
    }
  });
  return out;
}

/** Manual path: a flat count split across the topic's tier budget. */
function jobsForTopic(realm: string, topic: string, total: number): GenJob[] {
  const mix = TIER_MIX[tierOfTopic(topic)];
  const out: GenJob[] = [];
  (['easy', 'medium', 'hard'] as const).forEach((d, i) => {
    let n = Math.round(total * mix[i]);
    while (n > 0) {
      const c = Math.min(MAX_PER_CALL, n);
      out.push({ realm, topic, difficulty: d, count: c, label: `${realm} · ${topic} · ${d} ×${c}` });
      n -= c;
    }
  });
  return out;
}

/** The factory spends real money per call, so the edge function is admin-gated. */
async function adminToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Session expired. Sign in again to generate questions.');
  return session.access_token;
}

const BAR = (pct: number, color: string) => (
  <div style={{ height: 6, background: '#161616', borderRadius: 3, overflow: 'hidden' }}>
    <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, borderRadius: 3 }} />
  </div>
);

export function QuestionFactory() {
  const { realmStats, refreshStats, toast } = useAdmin();

  const [target, setTarget] = useState<CoverageTarget>(COVERAGE_TARGETS[0]);
  // Map is the default view: the first question is always "where are the
  // holes", and that is a question about the whole pool, not one realm.
  const [view, setView] = useState<'map' | 'realm'>('map');
  const [genRealm, setGenRealm] = useState<string>('ATLAS');
  const [selected, setSelected] = useState<string[]>([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTopic, setManualTopic] = useState('');
  const [manualCount, setManualCount] = useState(25);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; log: string[] } | null>(null);
  const abortRef = useRef(false);

  const gaps = useMemo(() => poolGaps(realmStats, target), [realmStats, target]);
  const current = useMemo(() => realmGap(genRealm, realmStats[genRealm], target), [genRealm, realmStats, target]);
  const poolHave = gaps.reduce((s, g) => s + g.have, 0);
  const poolNeed = gaps.reduce((s, g) => s + g.gap, 0);
  const realmsMet = gaps.filter(g => g.gap === 0).length;

  const runJobs = async (jobs: GenJob[], what: string) => {
    if (jobs.length === 0) { toast('Nothing to generate: the gap is already closed.', 'info'); return; }
    const total = jobs.reduce((s, j) => s + j.count, 0);
    if (!confirm(`${what}\n\n${total} questions across ${jobs.length} calls. This spends API credit. Continue?`)) return;

    abortRef.current = false;
    setRunning(true);
    const log: string[] = [];
    setProgress({ current: 0, total: jobs.length, log });
    let made = 0;

    for (let i = 0; i < jobs.length; i++) {
      if (abortRef.current) { log.push('durduruldu'); break; }
      const job = jobs[i];
      setProgress({ current: i + 1, total: jobs.length, log: [...log, `[${i + 1}/${jobs.length}] ${job.label}...`] });

      let ok = false;
      for (let attempt = 0; attempt < 2 && !ok; attempt++) {
        try {
          if (attempt > 0) await new Promise(r => setTimeout(r, 3000));
          const res: any = await invokeFunction('generate-questions', {
            realm: job.realm, topic: job.topic, difficulty: job.difficulty, count: job.count,
            surface_rule: tierOfTopic(job.topic) === 'GATEWAY' ? SURFACE_RULE : undefined,
            access_token: await adminToken(),
          });
          const dropped = Object.values(res.rejected || {}).reduce((a: number, b: any) => a + Number(b), 0);
          made += res.inserted || 0;
          log.push(`[${i + 1}] ${job.label} -> ${res.inserted} yeni`
            + (res.duplicates_skipped ? `, ${res.duplicates_skipped} tekrar` : '')
            + (dropped ? `, ${dropped} elendi` : ''));
          ok = true;
        } catch (err: any) {
          if (attempt === 0) continue;
          log.push(`[${i + 1}] ${job.label} -> HATA ${err.message}`);
        }
      }
      setProgress({ current: i + 1, total: jobs.length, log: [...log] });
      if (i < jobs.length - 1 && !abortRef.current) await new Promise(r => setTimeout(r, 2000));
    }
    setRunning(false);
    toast(`${made} soru uretildi`, 'success');
    refreshStats();
  };

  const topicsSorted = [...current.topics].sort((a, b) => b.gap - a.gap);

  return (
    <div>
      <h2 className="admin-section-title" style={{ marginBottom: 16 }}>QUESTION FACTORY</h2>

      {/* ═══ Pool health. The headline number is misleading on its own: the
          pool can clear a target in total while half the realms sit under it,
          so the realms-met count sits right beside it. ═══ */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {COVERAGE_TARGETS.map(t => {
            const active = t.key === target.key;
            const met = gaps.filter(g => g.have >= realmTarget(t, g.realm)).length;
            return (
              <button
                key={t.key}
                onClick={() => setTarget(t)}
                className="admin-btn admin-btn-sm"
                style={{
                  background: active ? '#c4973a' : '#16223A',
                  color: active ? '#000' : '#ccccd4',
                  border: `1px solid ${active ? '#c4973a' : '#33415E'}`,
                  fontWeight: active ? 700 : 500,
                }}
              >
                {t.label} · {t.perTopic}/konu · {met}/10 realm
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: '0.9rem', color: '#ccccd4', marginBottom: 14 }}>{target.meaning}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#eee', fontFamily: 'var(--font-mono)' }}>
            {poolHave.toLocaleString('tr-TR')}
          </span>
          <span style={{ color: '#bebec6' }}>/ {poolTarget(target).toLocaleString('tr-TR')} hedef</span>
          <span style={{ marginLeft: 'auto', color: poolNeed ? '#c4973a' : '#14b8a6', fontWeight: 600 }}>
            {poolNeed ? `${poolNeed.toLocaleString('tr-TR')} soru eksik` : 'hedef tamam'}
          </span>
        </div>
        {BAR((poolHave / poolTarget(target)) * 100, '#c4973a')}
        <div style={{ fontSize: '0.85rem', color: '#bebec6', marginTop: 8 }}>
          {realmsMet}/10 realm bu hedefte. Toplam sayı hedefi geçse bile realm'ler geçmemiş olabilir.
        </div>
      </div>

      {/* ═══ View switch. Map and realm are the same data at two zoom levels,
          both driven by the target above, which is why they belong on one
          page: the target is what decides whether a topic counts as a hole. ═══ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([['map', 'HARITA'], ['realm', 'REALM']] as const).map(([v, label]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className="admin-btn admin-btn-sm"
            style={{
              background: view === v ? '#c4973a' : '#16223A',
              color: view === v ? '#000' : '#ccccd4',
              border: `1px solid ${view === v ? '#c4973a' : '#33415E'}`,
              fontWeight: view === v ? 700 : 500,
            }}
          >
            {label}
          </button>
        ))}
        <span style={{ alignSelf: 'center', fontSize: '0.85rem', color: '#808080' }}>
          {view === 'map'
            ? 'Butun havuz. Realm basligina tikla, o realme in.'
            : `${genRealm} konu tablosu`}
        </span>
      </div>

      {view === 'map' && (
        <div className="admin-card" style={{ marginBottom: 16 }}>
          <CoverageGrid
            stats={realmStats}
            target={target}
            activeRealm={genRealm}
            onPickRealm={r => { setGenRealm(r); setSelected([]); setView('realm'); }}
            onPickTopic={(r, t) => {
              if (t.gap === 0) { toast(`${t.topic} zaten hedefte.`, 'info'); return; }
              setGenRealm(r);
              runJobs(jobsForGap(r, t), `${t.topic}: ${t.gap} soru uret`);
            }}
          />
          <button
            className="admin-btn"
            disabled={running || poolNeed === 0}
            style={{ marginTop: 16, width: '100%' }}
            onClick={() => runJobs(
              gaps.flatMap(g => g.topics.flatMap(t => jobsForGap(g.realm, t))),
              `Butun havuzu "${target.label}" hedefine tamamla`,
            )}
          >
            {poolNeed === 0 ? 'HEDEF TAMAM' : `TUM HAVUZU TAMAMLA (${poolNeed.toLocaleString('tr-TR')} soru)`}
          </button>
        </div>
      )}

      {/* ═══ Realm grid: gap first, count second ═══ */}
      {view === 'realm' && (
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <h3 className="admin-card-title" style={{ marginBottom: 12 }}>Realm durumu</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
          {[...gaps].sort((a, b) => b.gap - a.gap).map(g => {
            const sel = g.realm === genRealm;
            const done = g.gap === 0;
            return (
              <button
                key={g.realm}
                onClick={() => { setGenRealm(g.realm); setSelected([]); }}
                style={{
                  textAlign: 'left', padding: '12px 14px', cursor: 'pointer',
                  background: sel ? '#0E1626' : '#0d0d0d',
                  border: `1px solid ${sel ? (REALMS as any)[g.realm]?.color ?? '#c4973a' : '#2C3A58'}`,
                  borderRadius: 8,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem', color: (REALMS as any)[g.realm]?.color ?? '#eee' }}>
                    {g.realm}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: '#ccccd4' }}>
                    {g.have} / {g.target}
                  </span>
                </div>
                {BAR((g.have / g.target) * 100, done ? '#14b8a6' : '#c4973a')}
                <div style={{ fontSize: '0.82rem', marginTop: 7, color: done ? '#14b8a6' : '#c4973a', fontWeight: 600 }}>
                  {done ? 'hedef tamam' : `${g.gap} eksik`}
                </div>
                {(g.empty > 0 || g.thin > 0) && !done && (
                  <div style={{ fontSize: '0.8rem', color: '#bebec6', marginTop: 3 }}>
                    {g.empty > 0 && <span style={{ color: '#f43f5e' }}>{g.empty} konu bos</span>}
                    {g.empty > 0 && g.thin > g.empty && ' · '}
                    {g.thin > g.empty && `${g.thin - g.empty} konu ince`}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <button
          className="admin-btn"
          disabled={running || poolNeed === 0}
          style={{ marginTop: 14, width: '100%' }}
          onClick={() => runJobs(
            gaps.flatMap(g => g.topics.flatMap(t => jobsForGap(g.realm, t))),
            `Butun havuzu "${target.label}" hedefine tamamla`,
          )}
        >
          {poolNeed === 0 ? 'HEDEF TAMAM' : `TUM HAVUZU TAMAMLA (${poolNeed.toLocaleString('tr-TR')} soru)`}
        </button>
      </div>
      )}

      {/* ═══ Selected realm: the topic table, which is where the real answer
          lives. Sorted by gap, so the top row is always the next job. ═══ */}
      {view === 'realm' && (
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <h3 className="admin-card-title" style={{ margin: 0, color: (REALMS as any)[genRealm]?.color }}>{genRealm}</h3>
          <span style={{ fontSize: '0.85rem', color: '#ccccd4' }}>
            {current.have} soru · {current.gap ? `${current.gap} eksik` : 'hedef tamam'}
            {current.empty > 0 && <span style={{ color: '#f43f5e' }}> · {current.empty} konu bos</span>}
          </span>
        </div>
        <div style={{ fontSize: '0.85rem', color: '#bebec6', marginBottom: 12 }}>
          Eksik sayısı zorluk bandı başına hesaplanıyor. Hedefte görünen bir konu, her şeyi easy'deyse güçlü oyuncuya hâlâ hiçbir şey vermiyor.
        </div>

        <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #161616', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: '#0d0d0d', zIndex: 1 }}>
                <th style={{ width: 28, padding: '8px 6px' }}></th>
                <th style={{ textAlign: 'left', padding: '8px 6px', color: '#bebec6', fontWeight: 500 }}>KONU</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: '#bebec6', fontWeight: 500 }}>VAR</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: '#bebec6', fontWeight: 500 }}>HEDEF</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: '#bebec6', fontWeight: 500 }}>EKSIK</th>
                <th style={{ textAlign: 'right', padding: '8px 10px', color: '#bebec6', fontWeight: 500 }}>E / M / H</th>
              </tr>
            </thead>
            <tbody>
              {topicsSorted.map(t => {
                const checked = selected.includes(t.topic);
                return (
                  <tr
                    key={t.topic}
                    onClick={() => setSelected(p => p.includes(t.topic) ? p.filter(x => x !== t.topic) : [...p, t.topic])}
                    style={{ borderTop: '1px solid #141414', cursor: 'pointer', background: checked ? '#0E1626' : 'transparent' }}
                  >
                    <td style={{ padding: '7px 6px' }}>
                      <input type="checkbox" checked={checked} readOnly style={{ accentColor: '#c4973a', pointerEvents: 'none' }} />
                    </td>
                    <td style={{ padding: '7px 6px', color: t.have === 0 ? '#f43f5e' : '#e8e8ea' }}>
                      {t.topic}
                      <span style={{ color: '#4a4a4a', fontFamily: 'var(--font-mono)', fontSize: '0.8rem', marginLeft: 8 }}>
                        {t.tier}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: 'var(--font-mono)', color: t.have === 0 ? '#f43f5e' : '#d2d2d8' }}>{t.have}</td>
                    <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: 'var(--font-mono)', color: '#808080' }}>{t.target}</td>
                    <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: 'var(--font-mono)', fontWeight: 700, color: t.gap ? '#c4973a' : '#14b8a6' }}>
                      {t.gap || '-'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '7px 10px', fontFamily: 'var(--font-mono)', color: '#bebec6', fontSize: '0.82rem' }}>
                      {t.byDiff.join(' / ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            className="admin-btn admin-btn-sm"
            disabled={running || current.gap === 0}
            onClick={() => runJobs(current.topics.flatMap(t => jobsForGap(genRealm, t)), `${genRealm} realm'ini tamamla`)}
          >
            {genRealm} TAMAMLA ({current.gap})
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            disabled={running || selected.length === 0}
            onClick={() => runJobs(
              current.topics.filter(t => selected.includes(t.topic)).flatMap(t => jobsForGap(genRealm, t)),
              `${selected.length} secili konuyu tamamla`,
            )}
          >
            SECILILERI TAMAMLA ({selected.reduce((s, name) => s + (current.topics.find(t => t.topic === name)?.gap ?? 0), 0)})
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            disabled={running}
            onClick={() => setSelected(current.topics.filter(t => t.have === 0).map(t => t.topic))}
          >
            BOS KONULARI SEC ({current.empty})
          </button>
          {selected.length > 0 && (
            <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={running} onClick={() => setSelected([])}>
              TEMIZLE
            </button>
          )}
        </div>
      </div>
      )}

      {/* ═══ Progress ═══ */}
      {progress && (
        <div className="admin-card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: '#c4973a' }}>
              {progress.current} / {progress.total} cagri
            </span>
            {running && (
              <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => { abortRef.current = true; }}>
                DURDUR
              </button>
            )}
          </div>
          {BAR((progress.current / progress.total) * 100, '#c4973a')}
          <pre style={{
            background: '#0a0a0a', border: '1px solid #243450', padding: 10, borderRadius: 6, marginTop: 10,
            fontSize: '0.82rem', lineHeight: 1.6, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap',
          }}>{progress.log.join('\n')}</pre>
        </div>
      )}

      {/* ═══ Manual override, folded away. Still here for one-off jobs, but it
          is no longer the first thing the screen asks you to reason about. ═══ */}
      <div className="admin-card">
        <button
          onClick={() => setManualOpen(o => !o)}
          style={{ background: 'none', border: 0, color: '#ccccd4', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
        >
          {manualOpen ? '▾' : '▸'} Elle uretim
        </button>
        {manualOpen && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.85rem', color: '#bebec6', marginBottom: 10 }}>
              Hedeften bagimsiz, tek konuya belirli sayida soru. Zorluk yine konunun tier'ina gore bolunur.
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 260px' }}>
                <label className="admin-label">KONU</label>
                <select className="admin-select" value={manualTopic} onChange={e => setManualTopic(e.target.value)}>
                  <option value="">— {genRealm} icinden sec —</option>
                  {(TOPIC_CORE[genRealm] ?? []).map(t => (
                    <option key={t} value={t}>{t} ({realmStats[genRealm]?.subTopics?.[t] ?? 0})</option>
                  ))}
                </select>
              </div>
              <div style={{ width: 120 }}>
                <label className="admin-label">ADET</label>
                <input
                  className="admin-input" type="number" min={1} max={200} value={manualCount}
                  onChange={e => setManualCount(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
                />
              </div>
              <button
                className="admin-btn admin-btn-sm"
                disabled={running || !manualTopic}
                onClick={() => runJobs(jobsForTopic(genRealm, manualTopic, manualCount), `${manualTopic}: ${manualCount} soru`)}
              >
                URET
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
